import { useQuery } from '@tanstack/react-query';
import { fromTable } from '@/integrations/supabase/db';
import type { Tables } from '@/integrations/supabase/types';

type Kunde = Tables<'kunden'>;

type KundeBasic = Pick<Kunde, 'id' | 'vorname' | 'zuname' | 'email' | 'telefonnummer' | 'firmenname'>;
// ID-First: Include user_id for ownership lookups
type KundeExtended = Pick<Kunde, 'id' | 'firmenname' | 'vorname' | 'zuname' | 'email' | 'leadhelden_lead' | 'user_email' | 'user_id'>;
type KundeFull = Kunde;

interface UseKundenOptions {
  select?: 'basic' | 'extended' | 'full';
  limit?: number;
  search?: string;
  enabled?: boolean;
  /** Minimum characters required before fetching (for lazy search) */
  minSearchLength?: number;
}

export function useKunden(options?: UseKundenOptions) {
  const { select = 'basic', limit, search, enabled = true, minSearchLength = 0 } = options || {};

  // Only enable query if search meets minimum length requirement (for lazy loading)
  const searchEnabled = minSearchLength > 0 ? (search?.length || 0) >= minSearchLength : true;

  return useQuery({
    queryKey: ['kunden-list', select, limit, search],
    queryFn: async () => {
      let query = fromTable('kunden')
        .select(
          select === 'basic' 
            ? 'id, vorname, zuname, email, telefonnummer, firmenname'
            : select === 'extended'
            ? 'id, firmenname, vorname, zuname, email, leadhelden_lead, user_email, user_id'
            : `id, firmenname, vorname, zuname, email, telefonnummer, ort, status, leadhelden_lead, kundentyp, user_email, created_at,
               strasse, hausnummer, adresszusatz, postleitzahl, geburtsdatum, anrede, geschlecht,
               kommunikation, titel, alter, familienstand, berufliche_situation, beruf, geburtstagsmail, notizen,
               auskunftsvollmacht_url, ausweis_firmenbuchauszug_urls, sonstige_dokumente_urls,
               termindatetime, terminart, onlinemeetinglink, terminbestaetigung_versendet_am, schaden_gemeldet_am, datenerfassung_gestartet_am`
        )
        .order('vorname');

      if (search && search.trim()) {
        // Split search into individual terms and require ALL terms to match (AND logic)
        const terms = search.trim().toLowerCase().split(/\s+/).filter(t => t.length > 0);
        if (terms.length >= 1) {
          // Server-side search with first term, client-side will refine for multi-term
          query = query.or(`vorname.ilike.%${terms[0]}%,zuname.ilike.%${terms[0]}%,email.ilike.%${terms[0]}%,firmenname.ilike.%${terms[0]}%`);
        }
      }

      // Apply limit - for lazy search, use smaller limit
      const effectiveLimit = limit || (minSearchLength > 0 ? 50 : undefined);
      if (effectiveLimit) {
        query = query.limit(effectiveLimit);
      }

      const { data, error } = await query;

      if (error) throw error;
      return data as any as Array<{ id: string; vorname: string; zuname: string; email: string; telefonnummer: string; firmenname: string | null }>;
    },
    staleTime: 2 * 60 * 1000, // 2 minutes cache
    refetchOnMount: true,
    enabled: enabled && searchEnabled,
  });
}
